// @ts-check
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/apiClient';

const AdminRegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error on input change
  };

  const validateEmail = (email) => {
    // Must end with @kaay.co
    return email.toLowerCase().endsWith('@kaay.co');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.password || !formData.full_name) {
      setError('All fields are required');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Only @kaay.co email addresses are allowed');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/api/admin/auth/register', {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: 'admin',
      });

      alert('Admin account created successfully! Please log in.');
      navigate('/login');
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-serif mb-2">KAAY</h1>
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Admin Panel</p>
        </div>

        <div className="bg-card p-8 shadow-sm">
          <h2 className="text-2xl font-serif mb-6 text-center">Create Admin Account</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/50 border rounded-lg border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                placeholder="name@kaay.co"
              />
              <p className="text-xs text-muted-foreground mt-1">Must be a @kaay.co email address</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                placeholder="Minimum 8 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg border-input focus:outline-none focus:border-foreground"
                placeholder="Re-enter password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-primary-foreground uppercase tracking-wider hover:bg-primary/90 disabled:bg-muted-foreground/50 transition"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="underline hover:text-foreground">
                Sign In
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          <p>Admin accounts are restricted to @kaay.co email addresses only</p>
        </div>
      </div>
    </div>
  );
};

export default AdminRegisterPage;
